import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import {
    type ProviderConfigError,
    parseProviderConfigError,
    type TestResult,
    useTestProviderConfig,
} from '@/domain/providerConfig';
import { SERVER_ERROR } from '@/routes/shared/ProviderConfig/form/errorTypes';
import { serverErrorView } from '@/routes/shared/ProviderConfig/form/serverErrors';

import type { FormDiff } from '../form/formDiff';
import type { ProviderFormValues } from '../form/formModel';
import { buildTestInput } from '../form/saveInput';

import { applyFieldErrors } from './formPaths';
import { testScopeKey } from './testScope';

export interface ConnectionTest {
    result: TestResult | null;
    /** What the shown result tested has changed since, mappings included, so it may no longer hold. */
    stale: boolean;
    /** The token, while it still covers what the save would change; else null. */
    token: string | null;
    testing: boolean;
    error: ProviderConfigError | null;
    run: (cageIds: number[]) => Promise<void>;
    clear: () => void;
}

interface TestRun {
    result: TestResult;
    scope: string;
    cageIds: number[];
    /** The request the result answers. It holds typed secrets, so it stays in memory only. */
    input: string;
}

/**
 * The connection test and the token it may issue. The token is kept with the scope it
 * was issued for (testScopeKey), so changing the tested connection, a typed secret or a
 * pen's own override afterwards takes it away, while an edit to anything else keeps it. The
 * shown result is marked stale by any change to the request it answered, so a pen mapping
 * or SCADA list edited after the test no longer reads as tested, though the token stays.
 */
export function useConnectionTest(
    locationId: number,
    providerName: string,
    diff: FormDiff,
    form: UseFormReturn<ProviderFormValues>
): ConnectionTest {
    const { test, reset } = useTestProviderConfig(locationId, providerName);
    const [lastRun, setLastRun] = useState<TestRun | null>(null);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState<ProviderConfigError | null>(null);
    const scope = testScopeKey(diff);
    const token = lastRun !== null && lastRun.scope === scope ? lastRun.result.testToken : null;

    async function run(cageIds: number[]): Promise<void> {
        const input = buildTestInput(diff, cageIds);
        const scopeAtTest = testScopeKey(diff);
        const inputAtTest = JSON.stringify(input);
        setTesting(true);
        setError(null);
        try {
            const result = await test(input);
            setLastRun({ result, scope: scopeAtTest, cageIds, input: inputAtTest });
        } catch (failure) {
            const parsed = parseProviderConfigError(failure);
            setLastRun(null);
            setError(parsed);
            applyFieldErrors(form, serverErrorView(parsed).fields, SERVER_ERROR);
        } finally {
            // The outcome is copied above; the cached request still holds typed secrets.
            reset();
            setTesting(false);
        }
    }

    function clear(): void {
        setLastRun(null);
        setError(null);
    }

    const stale =
        lastRun !== null &&
        (lastRun.scope !== scope ||
            lastRun.input !== JSON.stringify(buildTestInput(diff, lastRun.cageIds)));
    return { result: lastRun?.result ?? null, stale, token, testing, error, run, clear };
}
